import { FetchError } from '@bank/fetch';

import type { FetchErrorInfo } from './types';

/**
 * Pull correlation/debug metadata out of a `FetchError`. Returns `undefined`
 * fields for any other error type.
 */
export const extractFromFetchError = (error: unknown): FetchErrorInfo => {
  if (error instanceof FetchError) {
    return {
      correlationId: error.correlationId,
      requestUrl: error.requestUrl,
      responseData: error.data,
    };
  }

  return {
    correlationId: undefined,
    requestUrl: undefined,
    responseData: undefined,
  };
};

/**
 * Marks an object so Sentry skips its internal normalization, preserving the
 * response payload as-is. No-op for non-object values.
 */
const addSentrySkipNormalization = (value: unknown): void => {
  if (typeof value === 'object' && value !== null) {
    Object.defineProperty(value, '__sentry_skip_normalization__', {
      configurable: true,
      value: true,
      writable: true,
    });
  }
};

/**
 * Build the Datadog message context (`axios_custom_context`) from the extracted
 * fetch-error metadata.
 */
export const getDatadogMessageContext = ({
  responseData,
  requestUrl,
  correlationId,
}: FetchErrorInfo) => {
  return {
    axios_custom_context: {
      request_url: requestUrl,
      response_data: responseData,
      x_correlation_id: correlationId,
    },
  };
};

/**
 * Build the Sentry capture hint (custom context + correlation tags) from the
 * extracted fetch-error metadata.
 */
export const getSentryHint = ({
  responseData,
  requestUrl,
  correlationId,
}: FetchErrorInfo) => {
  addSentrySkipNormalization(responseData);

  return {
    level: 'error' as const,
    contexts: {
      'Custom Context': {
        'Response data': responseData,
      },
    },
    tags: {
      request_url: requestUrl,
      x_correlation_id: correlationId,
    },
  };
};
